// src/components/FeatureShowcase.js
import React, { useState } from 'react';
import { IoSparklesOutline, IoCalendarOutline, IoAnalyticsOutline } from 'react-icons/io5';

// Define the content for each feature tab.
// The visual property now returns a React Fragment <>...</> instead of a div.
const features = {
  generate: {
    icon: IoSparklesOutline,
    title: "AI Content Generation",
    description: "Describe your campaign, and let Oolook's AI generate dozens of on-brand posts, complete with visuals and hashtags.",
    visual: (
      <>
        <IoSparklesOutline className="w-16 h-16 mb-4 text-dark-accent-start" />
        <p className="text-lg font-semibold text-dark-text-primary">AI-Powered Content Creation</p>
        <p className="text-sm text-dark-text-secondary/50 mt-2">Visual placeholder for generation animation</p>
      </>
    )
  },
  schedule: {
    icon: IoCalendarOutline,
    title: "Smart Scheduling",
    description: "Drag and drop your content onto our visual calendar. Oolook will automatically suggest the best times to post for maximum engagement.",
    visual: (
      <>
        <IoCalendarOutline className="w-16 h-16 mb-4 text-dark-accent-start" />
        <p className="text-lg font-semibold text-dark-text-primary">Visual Content Calendar</p>
        <p className="text-sm text-dark-text-secondary/50 mt-2">Visual placeholder for scheduling animation</p>
      </>
    )
  },
  analyze: {
    icon: IoAnalyticsOutline,
    title: "Actionable Analytics",
    description: "Go beyond likes and follows. Understand what drives growth with clear, beautiful reports that tell you what to do next.",
    visual: (
      <>
        <IoAnalyticsOutline className="w-16 h-16 mb-4 text-dark-accent-start" />
        <p className="text-lg font-semibold text-dark-text-primary">Insightful Performance Reports</p>
        <p className="text-sm text-dark-text-secondary/50 mt-2">Visual placeholder for analytics animation</p>
      </>
    )
  }
};

const FeatureShowcase = () => {
  const [activeFeature, setActiveFeature] = useState('generate');

  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Create. Schedule. Analyze. Grow.</h2>
          <p className="mt-4 text-lg text-dark-text-secondary">Visually discover how Oolook transforms your workflow.</p>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-8">
          {/* Left Column: Feature Selector Cards */}
          <div className="flex flex-col gap-6">
            {Object.entries(features).map(([key, feature]) => (
              <button
                key={key}
                onClick={() => setActiveFeature(key)}
                className={`
                  p-8 rounded-2xl text-left transition-all duration-300 ease-in-out border
                  bg-dark-card/40 backdrop-blur-lg transform-gpu hover:scale-[1.02] hover:border-dark-accent-start/50
                  ${activeFeature === key ? 'border-dark-accent-start/70 scale-[1.03]' : 'border-white/10'}
                `}
              >
                <feature.icon className="h-10 w-10 mb-4 text-dark-accent-start" />
                <h3 className="font-semibold text-lg text-dark-text-primary">{feature.title}</h3>
                <p className="text-sm mt-2 text-dark-text-secondary">{feature.description}</p>
              </button>
            ))}
          </div>

          {/* Right Column: Visual Display with Cross-Fade */}
          <div className="relative lg:col-span-2 h-[500px] bg-dark-card/30 rounded-2xl border border-white/10 p-8 shadow-inner">
            {Object.entries(features).map(([key, feature]) => (
              <div
                key={key}
                // ✅ THIS IS THE FIX: The outer container now handles all centering logic.
                className={`absolute inset-8 flex flex-col items-center justify-center text-center transition-opacity duration-500 ease-in-out
                  ${activeFeature === key ? 'opacity-100' : 'opacity-0'}
                `}
              >
                {feature.visual}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;